import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MdSend, MdClose, MdRefresh, MdAdd  } from "react-icons/md";
import ApiARIAChat, { TMessages } from '../../modules/api/aria-chat';
import ReactMarkdown from 'react-markdown';
import { useSearchParams } from 'react-router-dom';
import {BsArrowLeftCircle, BsArrowRightCircle } from 'react-icons/bs';
import { IoIosArrowBack, IoIosArrowDropright, IoIosArrowDroprightCircle, IoIosArrowForward } from 'react-icons/io';
import Utils from '../../utils/utils';
import ModulePopup from '../../modules/popup/popup';
import { RiDragMove2Line } from "react-icons/ri";

interface ARIAChatProps {
  show_close_button?: boolean;
  show_clear_chat_button?: boolean;
  closed_at_first?: boolean;
  stay_expanded?: boolean;
  stay_expanded_callback?: () => void;
  suggestions?: string[];
  is_supervisor_summary?: boolean;
  supervisor_summary?: string;
  custom_desc?: string | null;
  custom_title?: string | null;
  loading?: boolean;
  additionalPrompt?: string;
  setAdditionalPrompt?: React.Dispatch<React.SetStateAction<string>>;
  handleRegenerateFeedback?: () => void;
  is_sticky?: boolean;
  is_api?: boolean;
  cohortId?: string;
  studentId?: string;
  report?: Record<string, string>;
  prepareSectionUpdateForChatAnalysis?: (isFirstChat?: boolean) => Record<string, boolean>;
  is_supervisor?: boolean;
  is_student_dashboard?: boolean;
  has_aria_received_first_message?: boolean;
  setHasAriaReceivedFirstMessage?: React.Dispatch<React.SetStateAction<boolean>>;
  auto_report_analysis?: boolean;
  setAutoAriaReportAnalysis?: React.Dispatch<React.SetStateAction<boolean>>;
  hide_close_on_desktop?: boolean;
  bottomSuggestionButtons?: any[];
  sectionDescription?: { [key: string]: string };
  sectionMaxWord?: TPlainObject;
  student_reports_feedbacks?: any[];
  reports?: Array<{ submissionId: string, order: number }>;
  onHeaderMouseDown?: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  enable_drag?: boolean;
}

const ARIAChat : React.FC<ARIAChatProps> = ({
  show_close_button = true,
  show_clear_chat_button= false,
  closed_at_first = false,
  stay_expanded = false,
  stay_expanded_callback = () => {},
  suggestions = [],
  is_supervisor_summary = false,
  supervisor_summary = undefined,
  custom_desc = null,
  custom_title = null,
  loading = false,
  additionalPrompt = "",
  setAdditionalPrompt,
  handleRegenerateFeedback,
  is_sticky = false,
  is_api = false,
  cohortId = "",
  studentId = "",
  report = {},
  prepareSectionUpdateForChatAnalysis = () => { },
  is_supervisor = false,
  is_student_dashboard = false,
  has_aria_received_first_message = false,
  setHasAriaReceivedFirstMessage,
  auto_report_analysis,
  setAutoAriaReportAnalysis,
  hide_close_on_desktop = false,
  bottomSuggestionButtons = [],
  sectionDescription = {},
  sectionMaxWord = {},
  student_reports_feedbacks = [],
  reports = [],
  onHeaderMouseDown,
  isDragging = false,
  enable_drag = false,
}) => {
  const [messages, setMessages] = useState<TMessages[]>([]);
  const [inputText, setInputText] = useState('');
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [isClosed, setIsClosed] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState<boolean>(true);
  const [isDesktopView, setIsDesktopView] = useState(window.innerWidth > 767);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [model, setModel] = useState<string>(null);
  const [contentResult, setContentResult] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  const chatRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const name = "Hi, I'm ARIA";

  const getDefaultMessages = () => {
    if (is_api && suggestions.length > 0) {
      return suggestions.map((suggestion, index) => {
        const content = typeof suggestion === 'string' ? suggestion : (suggestion as any)?.text || '';
        const description = typeof suggestion === 'object' && suggestion && (suggestion as any)?.description ? (suggestion as any)?.description : null;
        return {
          content: content,
          description: description,
          type: "left" as const,
          isActionable: index > 0,
          isLastClicked: false,
          informational: !is_supervisor && index === 0
        };
      });
    } else if (is_api) {
      return [{
        content: "Hello, can I assist you today?",
        type: 'left' as const,
        isActionable: false,
        isLastClicked: false,
      }];
    } else {
      return [
        {
          content: "Hi Jim! Let's take it one step at a time. For \"Summary of Key Activities,\" think about the main tasks or projects you worked on recently. What were the most significant activities you completed? Do you have any difficulties?",
          type: 'left' as const,
          isActionable: false,
          isLastClicked: false,
        },
        {
          content: "I can't take it anymore. My supervisor just throws tasks at me without explaining anything, and then they get mad when I mess up. I feel so stupid.",
          type: 'right' as const,
          isActionable: false,
          isLastClicked: false,
        }
      ];
    }
  };

  useEffect(() => {
    setModel(searchParams.get("model"));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newIsDesktopView = window.innerWidth > 767;
      setIsDesktopView(newIsDesktopView);

      if (newIsDesktopView && hide_close_on_desktop && isClosed) {
        setIsClosed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [hide_close_on_desktop, isClosed]);

  useEffect(() => {
    if (isDesktopView && hide_close_on_desktop && isClosed) {
      setIsClosed(false);
    }
  }, [isDesktopView, hide_close_on_desktop, isClosed]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isClosed]);

  useEffect(() => {
    setIsClosed(closed_at_first && !(isDesktopView && hide_close_on_desktop));
  }, [closed_at_first, isDesktopView, hide_close_on_desktop]);

  useEffect(() => {
    if (bottomSuggestionButtons.length > 0) {
      setTimeout(() => {
        checkScrollButtons();
      }, 100);
    }
  }, [bottomSuggestionButtons]);

  useEffect(() => {
    custom_desc !== null && setDesc(custom_desc);
  }, [custom_desc]);

  useEffect(() => {
    custom_title !== null ?  setTitle(custom_title) : setTitle(name);
  }, [custom_title]);

  useEffect(() => {
    if (!isInitialized) {
      if (is_supervisor && studentId && cohortId) {
        const chatKey = getChatKey(cohortId, studentId);
        if (chatKey) {
          const saved = localStorage.getItem(chatKey);
          if (saved) {
            try {
              const savedMessages = JSON.parse(saved);
              setMessages(savedMessages);
            } catch (err) {
              console.error('Error loading saved messages:', err);
              setMessages(getDefaultMessages());
            }
          } else {
            setMessages(getDefaultMessages());
          }
        } else {
          setMessages(getDefaultMessages());
        }
      } else {
        setMessages(getDefaultMessages());
      }
      setIsInitialized(true);
    }
  }, [is_supervisor, studentId, cohortId, suggestions, is_api, isInitialized]);

  useEffect(() => {
    if (isInitialized && is_supervisor && studentId && cohortId) {
      const chatKey = getChatKey(cohortId, studentId);
      if (chatKey) {
        const saved = localStorage.getItem(chatKey);
        if (saved) {
          try {
            const savedMessages = JSON.parse(saved);
            setMessages(savedMessages);
          } catch (err) {
            console.error('Error loading saved messages:', err);
            setMessages(getDefaultMessages());
          }
        } else {
          setMessages(getDefaultMessages());
        }
      }
    }
  }, [studentId, cohortId, isInitialized, suggestions]);

  const refTimers = useRef<any>();

  // as of 2025-09-03, do nothing as requested by client
  // not to auto-analyze. maybe just trigger the focus event
  useEffect(() => {
    if (auto_report_analysis) {
      // handleSendMessage("Analyze my report");
      clearTimeout(refTimers.current);
      refTimers.current = setTimeout(() => inputRef.current?.focus(), 355);
      setAutoAriaReportAnalysis(false);
    }
  }, [auto_report_analysis]);

  useEffect(() => {
    const chatContainer = chatRef.current;
    if (!chatContainer) return;

    const elementStates = new Map();

    const handleScroll = () => {
      const stickyElements = document.querySelectorAll('.ac__chat__actionable-group--sticky');

      stickyElements.forEach((element) => {
        const chatRect = chatContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - chatRect.top;

        const currentState = elementStates.get(element) || { isStuck: false };

        const STICK_THRESHOLD = 0;
        const UNSTICK_THRESHOLD = 15;

        let shouldBeStuck;

        if (currentState.isStuck) {
          shouldBeStuck = relativeTop <= UNSTICK_THRESHOLD;
        } else {
          shouldBeStuck = relativeTop <= STICK_THRESHOLD;
        }

        if (shouldBeStuck !== currentState.isStuck) {
          currentState.isStuck = shouldBeStuck;
          elementStates.set(element, currentState);

          if (shouldBeStuck) {
            element.classList.add('is-stuck');
          } else {
            element.classList.remove('is-stuck');
          }
        }
      });
    };

    chatContainer.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [messages]);

  useEffect(() => {
    if (isInitialized && is_supervisor && studentId && cohortId && messages.length > 0) {
      const chatKey = getChatKey(cohortId, studentId);
      if (chatKey) {
        localStorage.setItem(chatKey, JSON.stringify(messages));
      }
    }
  }, [messages, cohortId, studentId, is_supervisor, isInitialized]);

  function getChatKey(cohortId: string, studentId: string) {
    if (!cohortId || !studentId) return null;
    return `growpro_${cohortId}_${studentId}`;
  }

  const scrollToBottom = () => {
    if (chatRef.current) {
      setTimeout(() => {
        if (chatRef.current) chatRef.current.scrollTop = chatRef?.current?.scrollHeight;
      }, 150);
    }
  };

  const handleSendMessage = async (message?: string) => {
    const finalMessage = message ?? inputText;

    if (!finalMessage.trim() || isTyping) return;

    setMessages(prev => [...prev, { content: finalMessage, type: 'right', isActionable: false, isLastClicked: false }]);

    inputRef.current?.focus();
    setInputText('');
    setIsTyping(true);

    if (!has_aria_received_first_message && setHasAriaReceivedFirstMessage) {
      setHasAriaReceivedFirstMessage(true);
    }

    const currentUpdateStatus = prepareSectionUpdateForChatAnalysis(isFirstMessage);
    setIsFirstMessage(false);

    let aria_messages = '';

    if (is_api) {
      const res = is_supervisor || is_student_dashboard
        ? await ApiARIAChat.analyzeSupervisorChat(messages, finalMessage, studentId, model, student_reports_feedbacks, reports)
        : await ApiARIAChat.analyzeChat(messages, finalMessage, studentId, report, currentUpdateStatus, model, contentResult, sectionDescription, sectionMaxWord);

      aria_messages = res.status ? res.data : res?.message ?? "An unexpected error occurred";
      setContentResult(res.contentResult || "");
    } else {
      aria_messages = finalMessage;
    }

    setTimeout(() => {
      const formattedMessage = Utils.dotToNewlines(aria_messages);

      setMessages(prev => [...prev, { content: formattedMessage, type: 'left', isActionable: false, isLastClicked: false }]);

      setIsTyping(false);
    }, 1500);
  };

  const handleSuggestionClick = (suggestion) => {
    if (!suggestion.trim() || isTyping) return;

    setIsTyping(true);
    handleSendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    if (is_supervisor && studentId && cohortId) {
      const chatKey = getChatKey(cohortId, studentId);
      if (chatKey) {
        localStorage.removeItem(chatKey);
      }
    }

    setMessages(getDefaultMessages());
    setIsFirstMessage(true);
  };

  const renderMarkdown = useMemo(() => {
    return (content) => {
      return (
        <ReactMarkdown>
          {content}
        </ReactMarkdown>
      );
    };
  }, []);

  const scrollHorizontally = (direction: string) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: 'smooth'
      });
      setTimeout(() => {
        checkScrollButtons();
      }, 300);
    }
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const groupMessages = (messages) => {
    const groups = [];
    let currentGroup = [];
    let isInActionableGroup = false;

    messages.forEach((message, index) => {
      const isActionableAlignedRight = message.isActionable && message.type === 'left';

      if (isActionableAlignedRight) {
        if (!isInActionableGroup) {
          if (currentGroup.length > 0) {
            groups.push({ type: 'regular', messages: currentGroup });
            currentGroup = [];
          }
          isInActionableGroup = true;
        }
        currentGroup.push({ ...message, originalIndex: index });
      } else {
        if (isInActionableGroup) {
          groups.push({ type: 'actionable', messages: currentGroup });
          currentGroup = [];
          isInActionableGroup = false;
        }
        currentGroup.push({ ...message, originalIndex: index });
      }
    });

    if (currentGroup.length > 0) {
      groups.push({
        type: isInActionableGroup ? 'actionable' : 'regular',
        messages: currentGroup
      });
    }

    return groups;
  };

  const shouldShowCloseButton = show_close_button && !isClosed && !(isDesktopView && hide_close_on_desktop);

  return (
    <div className={`aria-chat
      ${is_supervisor_summary ? 'aria-chat--supervisor_summary' : ''}
      ${isClosed ? 'aria-chat--closed' : ''}
      ${is_sticky ? 'aria-chat--sticky' : ''}
      `}
         data-comp="ARIAChat">
      <div className={`ac__header
        ${is_supervisor_summary ? 'ac__header--long-bg' : ''}`}
        onMouseDown={onHeaderMouseDown}
        style={{ cursor: isDragging ? 'move' : 'default' }}
      >

        {!is_supervisor_summary ? <img src="/assets/images/aria-profile.png?ver=3" alt={title} onClick={isClosed ? () => setIsClosed(false) : () => {}} />: null}
        <div className='ac__desc'>
          <h1>{!is_supervisor_summary ? title : custom_desc ? custom_desc : "ARIA"}</h1>
          {!is_supervisor_summary ? <p>{desc}</p> : null}
        </div>
        {show_clear_chat_button &&
          <button className="ac__clear-btn"
            onClick={() => {
              ModulePopup.showAlertConfirm({
                description: "Are you sure you want to clear the chat? This action cannot be undone.",
                onYes() {
                  handleClearChat();
                }
              })
            }}
            aria-label="Clear Chat"
          >
            Clear Chat
          </button>
        }
        {enable_drag && <span className="ac__drag-icon">
          <RiDragMove2Line />
        </span>}
        {shouldShowCloseButton  &&
          <button className="ac__close-btn"
            onClick={!isClosed ? () => {
              stay_expanded ? stay_expanded_callback() : setIsClosed(true);
            } : () => {}}
            aria-label='Close chat'>
            <MdClose />
          </button>
        }
      </div>

      <div className='ac__chat' ref={chatRef}>
        <div className='ac__chat__wrapper'>
          {!is_supervisor_summary ? (
            groupMessages(messages).map((group, groupIndex) => {
              if (group.type === 'actionable') {
                return (
                  <div
                    key={`actionable-group-${groupIndex}`}
                    className="ac__chat__actionable-group ac__chat__actionable-group--sticky"
                  >
                    {group.messages.map((message, messageIndex) => (
                      <div
                        key={`${groupIndex}-${messageIndex}`}
                        className={`ac__chat__message ac__chat__message--${message.type} align-right`}
                      >
                        <div
                          className={`ac__chat__message__content ac__chat__message__content--actionable ${message.isLastClicked ? 'ac__chat__message__content--last-clicked' : ''}`}
                          onClick={() => {
                            const updatedMessages = messages.map(msg => ({
                              ...msg,
                              isLastClicked: msg.content === message.content
                            }));
                            setMessages(updatedMessages);
                            handleSuggestionClick(message.content);
                          }}
                        >
                          <div className="markdown-content">
                            {renderMarkdown(message.content)}
                          </div>
                        </div>
                        {message.description && (
                          <div className="button-description">
                            {message.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              } else {
                return group.messages.map((message, messageIndex) => (
                  <div
                    key={`regular-${groupIndex}-${messageIndex}`}
                    className={`ac__chat__message ac__chat__message--${message.type} ${message.isActionable ? 'align-right' : ''}`}
                  >
                    <div
                      className={`ac__chat__message__content ${message.informational ? 'ac__chat__message__content--informational' : ''}`}
                      onClick={message.isActionable ? () => {
                        const updatedMessages = messages.map(msg => ({
                          ...msg,
                          isLastClicked: msg.content === message.content
                        }));
                        setMessages(updatedMessages);
                        handleSuggestionClick(message.content);
                      } : () => {}}
                    >
                      <div className="markdown-content">
                        {renderMarkdown(message.content)}
                      </div>
                    </div>
                  </div>
                ));
              }
            })
          ) : (
            <>
              <div className='ac__chat__supervisor__input'>
                <label htmlFor="additional_system_prompt">Additional System Prompt</label>
                <textarea
                  name="additional_system_prompt"
                  id="additional_system_prompt"
                  placeholder=""
                  rows={2}
                  value={additionalPrompt}
                  onChange={(e) => setAdditionalPrompt(e.target.value)}
                />
              </div>
              {!loading ? (
                <div className='supervisor_summary-text'>
                  <ReactMarkdown>{supervisor_summary}</ReactMarkdown>
                </div>
              ) : (
                <div className='ac__chat__typing'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </>
          )}
          {isTyping && (
            <div className='ac__chat__message ac__chat__message--left'>
              <div className='ac__chat__message__content ac__chat__typing'>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        </div>
      </div>

      {!is_supervisor_summary ? <div className='ac__action' onClick={isClosed ? () => setIsClosed(false) : () => { }}
        style={bottomSuggestionButtons.length === 0 ? {paddingBottom: "5px"} : {}}
      >
        <input
          ref={inputRef}
          type='text'
          placeholder='Type your message here...'
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyPress}
          autoComplete='off'
          name='user_input'
        />
        <button onClick={() => { handleSendMessage() }}><MdSend style={ isTyping ? {color: "#ccc"} : {color: "#2E3633"}} /></button>
      </div>
        :
        <div className='supervisor_summary-buttons'>
          {false && <span><MdAdd /> Add feedback</span>}
          {false && <span>|</span>}
          <span onClick={handleRegenerateFeedback}><MdRefresh /> Generate again</span>
        </div>
      }

      {!is_supervisor_summary && bottomSuggestionButtons.length > 0 && (
        <div className='ac__bottom-suggestions'>
          <div
            className='ac__bottom-suggestions__container'
            ref={scrollContainerRef}
            onScroll={checkScrollButtons}
          >
            {bottomSuggestionButtons.map((button, index) => (
              <button
                key={index}
                className='ac__bottom-suggestions__button'
                onClick={() => handleSuggestionClick(button)}
                disabled={isTyping}
              >
                {button}
              </button>
            ))}
          </div>

          {canScrollLeft && (
            <button
              className='ac__bottom-suggestions__scroll-btn ac__bottom-suggestions__scroll-btn--left'
              onClick={() => scrollHorizontally("left")}
              aria-label="Scroll Left"
            >
              <IoIosArrowBack className='rounded-icon'/>
            </button>
          )}

          {canScrollRight && (
            <button
              className='ac__bottom-suggestions__scroll-btn ac__bottom-suggestions__scroll-btn--right'
              onClick={() => scrollHorizontally("right")}
              aria-label="Scroll Right"
            >
              <IoIosArrowForward className='rounded-icon'/>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ARIAChat;
